import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'
import { db } from '../firebase'
import { useTheme } from '../context/ThemeContext'

export function getCollectionName (listType, theme) {
  return theme === 'okb' ? `accessibility-okb-${listType}` : `accessibility-${listType}`
}

export function useListData (sortField, sortOrder) {
  const [data, setData] = useState([])
  const { listType } = useParams()
  const { theme } = useTheme()

  useEffect(() => {
    const fetchData = async () => {
      const q = query(
        collection(db, getCollectionName(listType, theme)),
        orderBy('config.timestamp', 'desc')
      )
      const querySnapshot = await getDocs(q)

      // If the collection is empty, just set an empty array and return
      if (querySnapshot.empty) {
        setData(null)
        return
      }

      const lists = querySnapshot.docs.map((doc) => {
        const dataDoc = doc.data()
        const config = dataDoc.config || {}
        const timestamp = config.timestamp || { toDate: () => new Date(0) }

        if (listType === 'projects') {
          return {
            id: doc.id,
            client: config.client || '',
            user: config.user || '',
            name: config.name || 'Unnamed',
            timestamp,
            webpages: config.webpages || [],
            url: config.url || '',
            scope: config.scope || '',
            environment: config.environment || '',
            originalFileName: config.originalFileName || ''
          }
        } else {
          return {
            id: doc.id,
            client: config.client || '',
            user: config.user || '',
            name: config.name || 'Unnamed',
            timestamp,
            webpages: config.webpages || [],
            url: config.url || '',
            idJira: config.idJira || '',
            environment: config.environment || '',
            originalFileName: config.originalFileName || ''
          }
        }
      })

      // Sort the data (make sure you're sorting the projectsData, not the old state)
      lists.sort((a, b) => {
        if (sortField === 'name') {
          return sortOrder === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name)
        } else if (sortField === 'urls') {
          // 'urls' presumably means the length of the webpages array
          return sortOrder === 'asc'
            ? a.webpages.length - b.webpages.length
            : b.webpages.length - a.webpages.length
        } else if (sortField === 'environment') {
          return sortOrder === 'asc'
            ? a.environment.localeCompare(b.environment)
            : b.environment.localeCompare(a.environment)
        } else if (sortField === 'users') {
          return sortOrder === 'asc'
            ? a.user.localeCompare(b.user)
            : b.user.localeCompare(a.user)
        }
        return 0
      })

      setData(lists)
    }

    fetchData()
  }, [sortField, sortOrder, listType, theme])

  return data
}

export function useProjects (sortField, sortOrder) {
  const [projects, setProjects] = useState([])
  const { listType } = useParams()
  const { theme } = useTheme()

  useEffect(() => {
    const fetchProjects = async () => {
      const q = query(
        collection(db, getCollectionName('projects', theme)),
        orderBy('config.timestamp', 'desc')
      )
      const querySnapshot = await getDocs(q)
      const projectsData = querySnapshot.docs.map((doc) => {
        const data = doc.data()
        const config = data.config || {}
        const timestamp = config.timestamp || { toDate: () => new Date(0) }
        return {
          id: doc.id,
          client: config.client || '',
          user: config.user || '',
          name: config.name || 'Unnamed',
          timestamp,
          webpages: config.webpages || [],
          url: config.url || '',
          scope: config.scope || '',
          environment: config.environment || '',
          originalFileName: config.originalFileName || ''
        }
      })

      // Sorting logic
      projectsData.sort((a, b) => {
        if (sortField === 'name') {
          return sortOrder === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name)
        } else if (sortField === 'urls') {
          return sortOrder === 'asc'
            ? a.webpages.length - b.webpages.length
            : b.webpages.length - a.webpages.length
        } else if (sortField === 'environment') {
          return sortOrder === 'asc'
            ? a.environment.localeCompare(b.environment)
            : b.environment.localeCompare(a.environment)
        } else if (sortField === 'users') {
          return sortOrder === 'asc'
            ? a.user.localeCompare(b.user)
            : b.user.localeCompare(a.user)
        }
        return 0
      })

      setProjects(projectsData)
    }

    fetchProjects()
  }, [sortField, sortOrder, listType, theme])

  return projects
}

export function useTasks (sortField, sortOrder) {
  const [tasks, setTasks] = useState([])
  const { listType } = useParams()
  const { theme } = useTheme()

  useEffect(() => {
    const fetchTasks = async () => {
      const q = query(
        collection(db, getCollectionName('tasks', theme)),
        orderBy('config.timestamp', 'desc')
      )
      const querySnapshot = await getDocs(q)
      const tasksData = querySnapshot.docs.map((doc) => {
        const dataDoc = doc.data()
        const config = dataDoc.config || {}
        const timestamp = config.timestamp || { toDate: () => new Date(0) }
        return {
          id: doc.id,
          client: config.client || '',
          user: config.user || '',
          name: config.name || 'Unnamed',
          timestamp,
          webpages: config.webpages || [],
          url: config.url || '',
          idJira: config.idJira || '',
          environment: config.environment || '',
          originalFileName: config.originalFileName || ''
        }
      })

      // Sorting logic
      tasksData.sort((a, b) => {
        if (sortField === 'name') {
          return sortOrder === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name)
        } else if (sortField === 'urls') {
          return sortOrder === 'asc'
            ? a.webpages.length - b.webpages.length
            : b.webpages.length - a.webpages.length
        } else if (sortField === 'environment') {
          return sortOrder === 'asc'
            ? a.environment.localeCompare(b.environment)
            : b.environment.localeCompare(a.environment)
        } else if (sortField === 'users') {
          return sortOrder === 'asc'
            ? a.user.localeCompare(b.user)
            : b.user.localeCompare(a.user)
        }
        return 0
      })

      setTasks(tasksData)
    }

    fetchTasks()
  }, [sortField, sortOrder, listType, theme])

  return tasks
}
